/* ResumenEjecutivoPipas.jsx — Vista de solo lectura para el rol coordinador_pipas.
   Muestra: actividad del día + estatus de flotilla con pipas en taller.
   Ruta: /resumen-ejecutivo
*/
import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function fechaLegible(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
  } catch { return iso; }
}

function Badge({ children, color }) {
  const paleta = {
    verde:    { bg: "#DCFCE7", txt: "#15803D", borde: "#BBF7D0" },
    rojo:     { bg: "#FEE2E2", txt: "#B91C1C", borde: "#FCA5A5" },
    amarillo: { bg: "#FEF3C7", txt: "#92400E", borde: "#FDE68A" },
    gris:     { bg: "#F1F5F9", txt: "#475569", borde: "#E2E8F0" },
  }[color] || paleta.gris;
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 999,
      background: paleta.bg, color: paleta.txt,
      border: `1px solid ${paleta.borde}`,
      fontWeight: 700, fontSize: 12,
    }}>
      {children}
    </span>
  );
}

function Stat({ label, value, sub, color }) {
  const colores = { azul: "#1D4ED8", verde: "#15803D", rojo: "#B91C1C", slate: "#475569" };
  return (
    <div style={{
      background: "white", borderRadius: 12, padding: "16px 20px",
      border: "1px solid #E2E8F0", flex: 1, minWidth: 130,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 900, color: colores[color] || "#0F172A", lineHeight: 1.1, marginTop: 4 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ResumenEjecutivoPipas() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [cargando, setCargando] = useState(true);
  const fecha = hoyISO();

  useEffect(() => {
    const cargar = async () => {
      try {
        const r = await axios.get(`${API}/supervisor/reporte-diario`, {
          params: { fecha },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Error al cargar datos");
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [fecha]);

  const res       = data?.resumen || {};
  const alertas   = data?.alertas || {};
  const flotilla  = res.estatus_flotilla || {};
  const desglose  = res.desglose_por_tipo || {};
  const tallerLista = alertas.pipas_en_taller || [];
  const operativas  = flotilla.total_operativas ?? res.pipas_activas ?? 0;
  const enTaller    = flotilla.total_en_taller  ?? res.pipas_taller  ?? 0;
  const total       = flotilla.total_operacional ?? res.pipas_total   ?? 0;
  const otrosEstados = flotilla.otros_estados || {};
  const totalInterv = res.total_intervenciones ?? 0;

  // Día legible
  let diaLabel = fecha;
  try {
    const d = new Date(fecha + "T12:00:00");
    const DIAS = ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"];
    diaLabel = `${DIAS[d.getDay()]} ${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`;
  } catch {}

  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC", padding: "24px 16px 48px" }}>
      {/* Encabezado */}
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10, background: "#0F172A",
            display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
            border: "2px solid #C9A84C", flexShrink: 0,
          }}>
            <span style={{ color: "#C9A84C", fontWeight: 900, fontSize: 12, lineHeight: 1 }}>DMU</span>
            <span style={{ color: "white", fontWeight: 800, fontSize: 10 }}>614</span>
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#0F172A" }}>Resumen ejecutivo</div>
            <div style={{ fontSize: 13, color: "#64748B" }}>{diaLabel}</div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: "#94A3B8" }}>
              Hola, {user?.nombre?.split(" ")[0] || user?.usuario}
            </span>
            <button
              onClick={logout}
              data-testid="btn-logout"
              title="Cerrar sesión"
              style={{
                width: 38, height: 38, borderRadius: 8,
                background: "#0F172A", border: "none",
                color: "white", fontSize: 15,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", flexShrink: 0,
              }}
            >
              <i className="fa-solid fa-right-from-bracket" />
            </button>
          </div>
        </div>

        {cargando && (
          <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 28, marginBottom: 12, display: "block" }} />
            Cargando datos…
          </div>
        )}

        {error && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 10, padding: 16, color: "#991B1B", marginBottom: 20 }}>
            <i className="fa-solid fa-triangle-exclamation mr-2" />{error}
          </div>
        )}

        {data && (
          <>
            {/* ── Actividad del día ── */}
            <section style={{ marginBottom: 24 }}>
              <div style={S.secLabel}>Actividad del día</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Stat
                  label="Total eventos"
                  value={totalInterv}
                  sub="registros del bot"
                  color={totalInterv > 0 ? "azul" : "slate"}
                />
                <Stat
                  label="Choferes activos"
                  value={res.choferes_activos ?? 0}
                  color="verde"
                />
              </div>

              {Object.keys(desglose).length > 0 && (
                <div style={{ marginTop: 12, background: "white", borderRadius: 12, border: "1px solid #E2E8F0", overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", borderBottom: "1px solid #F1F5F9", fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    Desglose por tipo
                  </div>
                  {Object.entries(desglose)
                    .sort(([,a],[,b]) => b - a)
                    .map(([tipo, cnt]) => (
                      <div key={tipo} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 16px", borderBottom: "1px solid #F8FAFC" }}>
                        <span style={{ fontSize: 13, color: "#334155" }}>{tipo}</span>
                        <span style={{ fontWeight: 800, fontSize: 14, color: "#0F172A" }}>{cnt}</span>
                      </div>
                    ))
                  }
                </div>
              )}

              {totalInterv === 0 && (
                <div style={{ marginTop: 10, padding: "10px 14px", background: "#F8FAFC", borderRadius: 8, fontSize: 13, color: "#94A3B8", textAlign: "center" }}>
                  Sin actividad registrada hoy
                </div>
              )}
            </section>

            {/* ── Estatus de flotilla ── */}
            <section style={{ marginBottom: 24 }}>
              <div style={S.secLabel}>Estatus de flotilla</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <Stat label="Operativas"  value={operativas} color="verde" sub={`de ${total} en operación`} />
                <Stat label="En taller"   value={enTaller}   color={enTaller > 0 ? "rojo" : "slate"} />
                {Object.entries(otrosEstados).map(([estado, cnt]) => (
                  <Stat key={estado} label={estado} value={cnt} color="amarillo" />
                ))}
              </div>

              {/* Lista de pipas en taller */}
              {enTaller > 0 ? (
                <div style={{ background: "white", borderRadius: 12, border: "1px solid #E2E8F0", overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", borderBottom: "1px solid #F1F5F9", fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    Pipas en taller ({enTaller})
                  </div>
                  {tallerLista.length > 0 ? tallerLista.map((p) => (
                    <div key={p.id_pipa} style={{ padding: "12px 16px", borderBottom: "1px solid #F8FAFC" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontWeight: 800, fontSize: 14, color: "#0F172A" }}>
                          Pipa #{p.id_pipa}
                        </span>
                        <Badge color="rojo">EN TALLER</Badge>
                      </div>
                      <div style={{ fontSize: 12, color: "#64748B" }}>
                        {p.en_taller_motivo
                          ? <><i className="fa-solid fa-wrench mr-1" />{p.en_taller_motivo}</>
                          : <span style={{ color: "#CBD5E1" }}>Sin motivo registrado</span>
                        }
                      </div>
                      {p.en_taller_desde && (
                        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>
                          <i className="fa-regular fa-clock mr-1" />Desde: {fechaLegible(p.en_taller_desde)}
                        </div>
                      )}
                      {p.zona && (
                        <div style={{ fontSize: 11, color: "#94A3B8" }}>
                          <i className="fa-solid fa-location-dot mr-1" />{p.zona}
                        </div>
                      )}
                    </div>
                  )) : (
                    <div style={{ padding: "12px 16px", fontSize: 13, color: "#94A3B8" }}>
                      El sistema indica {enTaller} en taller pero no hay detalle disponible.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: "12px 16px", background: "#F0FDF4", borderRadius: 10, border: "1px solid #BBF7D0", fontSize: 13, color: "#15803D", textAlign: "center" }}>
                  <i className="fa-solid fa-circle-check mr-2" />Todas las pipas operativas — ninguna en taller
                </div>
              )}

              {/* Reserva especial */}
              {(flotilla.reserva_especial || []).length > 0 && (
                <div style={{ marginTop: 10, padding: "10px 14px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 12, color: "#92400E" }}>
                  <i className="fa-solid fa-star mr-1" />
                  <strong>Reserva especial:</strong> Pipa #{flotilla.reserva_especial[0].id_pipa} — {flotilla.reserva_especial[0].nota}
                </div>
              )}
            </section>

            <div style={{ fontSize: 11, color: "#CBD5E1", textAlign: "center", marginTop: 8 }}>
              Vista de solo lectura · Generado {new Date().toLocaleTimeString("es-MX")}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  secLabel: {
    fontSize: 11, fontWeight: 700, color: "#94A3B8",
    textTransform: "uppercase", letterSpacing: 0.6,
    marginBottom: 10,
  },
};
